import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
// Components
import App from '../../../components/app';
import EnhancedTable from '../../../components/table';
// Modules
import { tableRequestPoints } from '../../../redux/reducers/constants';
import { actions as driverActions, selectors as driverSelectors } from '../../../redux/reducers/drivers';
// Ressources
import FormattingUtils from '../../../utils/formattingUtils';
import ObjectUtils from '../../../utils/objectUtils';
import { AccountRight } from '../../../utils/userUtils';
import { DispatchGroup } from '../../../redux/reducers/api-types';
import { generateOptionsString, getDispatchGroupOptions } from '../../../utils/businessUtils';
import StringUtils from '../../../utils/stringUtils';
import { getFindUsersURL } from '../../../utils/core-api';
import { FormatType, HeadCell, PickerOption } from '../../../local-types';
import { Cell, createCell } from '../../../components/table/table-cell-creator';

const queryString = require('query-string');

interface ReservationsListProps extends WithTranslation, RouteComponentProps {
    dispatchGroups: DispatchGroup[];
    getDispatchGroups: () => void;
}

class ReservationsListPage extends React.Component<ReservationsListProps> {
    constructor(props: ReservationsListProps) {
        super(props);

        const { dispatchGroups } = this.props;
        if (dispatchGroups.length === 0) {
            this.props.getDispatchGroups();
        }
    }

    generateFilterParams = (): any => {
        // const nowFormat = DateUtils.dateToString(new Date());

        return ObjectUtils.isEmpty(queryString.parse(this.props.location.search))
            ? {
                  //   'after[planned_start_date]': nowFormat,
              }
            : queryString.parse(this.props.location.search);
    };

    generateStatusOptions = (): PickerOption[] => {
        return generateOptionsString(
            this.props.t,
            ['created', 'booking_done', 'splitted', 'cancelled_no_charge', 'cancelled_charge'],
            'status.',
        );
    };

    headerBuilder = (): HeadCell[] => {
        const { t, dispatchGroups } = this.props;
        const headers: HeadCell[] = [
            // createCell(t, Cell.ID, { sortable: true }),
            { id: 'number', label: t('common:form.number'), sortable: false, contains: true, minLength: 4 },
            {
                id: 'rider.fullname',
                label: t('rider'),
                formatCell: function (row) {
                    return (
                        <>
                            <div style={{ fontWeight: 600 }}>{row.rider.fullname}</div>
                        </>
                    );
                },
            },
            createCell(t, Cell.COMPANY, {
                id: 'company.name',
                sortable: true,
                filterable: true,
                formatCell: function (row) {
                    return (
                        <>
                            {row.company?.name ? (
                                <div style={{ marginLeft: 8, fontStyle: 'italic' }}>{row.company.name}</div>
                            ) : null}
                        </>
                    );
                },
            }),
            {
                id: 'booker.fullname',
                label: t('booker'),
                formatCell: function (row) {
                    return (
                        <>
                            <div style={{ fontWeight: 600 }}>{row.booker?.fullname || row.rider?.fullname}</div>
                        </>
                    );
                },
            },
            {
                id: 'favored_driver.fullnameOrId',
                label: t('favored_driver'),
                formatCell: function (row) {
                    return (
                        <>
                            {row.favored_driver?.fullname && (
                                <div
                                    style={{ marginLeft: 8 }}
                                >{`${row.favored_driver.fullname} (${row.favored_driver.id})`}</div>
                            )}
                        </>
                    );
                },
            },
            {
                id: 'status',
                label: t('status.title'),
                contains: false,
                type: FormatType.PickerMultiple,
                pickerOptions: this.generateStatusOptions(),
                formatCell: function (row) {
                    const translation = t('status.' + row.status);
                    return translation;
                },
            },
            {
                id: 'order',
                label: t('order'),
                formatCell: function (row) {
                    if (!row.order || !row.order.base_price_without_vat || !row.order.base_price_with_vat) {
                        return null;
                    }
                    return (
                        <>
                            <div>{`${FormattingUtils.price(
                                row.order.base_price_without_vat,
                            )} HT (${FormattingUtils.price(row.order.base_price_with_vat)} TTC)`}</div>
                        </>
                    );
                },
                filterable: false,
                sortable: false,
            },
            createCell(t, Cell.CREATION_DATE, { defaultSort: true }),
            {
                id: 'planned_start_date',
                label: t('planned_start_date'),
                type: FormatType.DateLong,
                filterable: false,
                interval: true,
                intervalDateTime: true,
            },
            {
                id: 'planned_start_location',
                label: t('planned_start_location'),
                type: FormatType.Address,
                filterable: false,
                sortable: false,
            },
            {
                id: 'planned_end_location',
                label: t('planned_end_location'),
                type: FormatType.Address,
                filterable: false,
                sortable: false,
            },
            {
                id: 'dispatch_group',
                label: t('dispatch_group'),
                filterable: dispatchGroups.length > 0,
                type: FormatType.Picker,
                pickerOptions: getDispatchGroupOptions(dispatchGroups),
                formatCell: function (row) {
                    return `${row.dispatch_group?.name ?? ''}`;
                },
            },
            {
                id: 'company.account_manager.id',
                label: t('account_manager'),
                sortable: false,
                type: FormatType.Autocomplete,
                api: getFindUsersURL(),
                customAlt: (item) => item?.id,
                formatCell: function (row) {
                    const { company } = row;
                    return StringUtils.userFullname(company?.account_manager);
                },
            },
            { id: 'reason', label: t('ride_reason'), sortable: false },
            {
                id: 'reference',
                label: t('external_reference'),
                sortable: false,
                contains: true,
                minLength: 3,
                defaultVisibility: false,
            },
        ];
        return headers;
    };

    // generateActions = (): ActionFunction[] => {
    //     const { t } = this.props;

    //     const canCreate = UserUtils.checkRightsOrAdmin(AccountRight.RIDE_ACTION_CREATE);
    //     let actions: ActionFunction[] = [];
    //     if (canCreate) {
    //         actions.push({
    //             label: t('recurrents.list'),
    //             func: () => this.props.history.push(paths.reservationsRecurrents),
    //         });
    //         actions.push({
    //             label: t('recurrents.add'),
    //             func: () => this.props.history.push(paths.reserationsAdd),
    //         });
    //     }
    //     return actions;
    // };

    render() {
        const { t } = this.props;

        return (
            <App
                disableBreadcrumb
                needRights={AccountRight.RIDE_VIEW}
                header={{
                    title: t('title'),
                    subtitle: t('description'),
                    // actions: this.generateActions(),
                }}
            >
                <EnhancedTable
                    headers={this.headerBuilder()}
                    type={tableRequestPoints.RESERVATIONS}
                    filterParams={this.generateFilterParams()}
                />
            </App>
        );
    }
}

const mapStateToProps = (state) => ({
    dispatchGroups: driverSelectors.sortedDispatchGroup(state),
});

const mapDispatchToProps = {
    getDispatchGroups: driverActions.getDispatchGroups,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withTranslation(['reservation', 'common'])(ReservationsListPage));
